import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedSocialMediaManager = ({ clubSettings, setClubSettings }) => {
  const [socialMedia, setSocialMedia] = useState([
    {
      id: 'x',
      name: 'X (تويتر سابقاً)',
      key: 'x_url',
      url: '',
      icon: null,
      uploadedFile: null, // للمعاينة المؤقتة
      color: '#000000',
      placeholder: 'https://x.com/alamjadclub',
      defaultIcon: '𝕏'
    },
    {
      id: 'instagram',
      name: 'إنستغرام',
      key: 'instagram_url',
      url: '',
      icon: null,
      uploadedFile: null,
      color: '#E4405F',
      placeholder: 'https://instagram.com/alamjadclub',
      defaultIcon: '📷'
    },
    {
      id: 'youtube',
      name: 'يوتيوب',
      key: 'youtube_url',
      url: '',
      icon: null,
      uploadedFile: null,
      color: '#FF0000',
      placeholder: 'https://youtube.com/@alamjadclub',
      defaultIcon: '📺'
    },
    {
      id: 'facebook',
      name: 'فيسبوك',
      key: 'facebook_url',
      url: '',
      icon: null,
      uploadedFile: null,
      color: '#1877F2',
      placeholder: 'https://facebook.com/alamjadclub',
      defaultIcon: '👥'
    },
    {
      id: 'tiktok',
      name: 'تيك توك',
      key: 'tiktok_url',
      url: '',
      icon: null,
      uploadedFile: null,
      color: '#000000',
      placeholder: 'https://tiktok.com/@alamjadclub',
      defaultIcon: '🎵'
    },
    {
      id: 'snapchat',
      name: 'سناب شات',
      key: 'snapchat_url',
      url: '',
      icon: null,
      uploadedFile: null,
      color: '#FFFC00',
      placeholder: 'https://snapchat.com/add/alamjadclub',
      defaultIcon: '👻'
    }
  ]);

  const [uploading, setUploading] = useState({});

  useEffect(() => {
    // Load existing social media settings
    if (clubSettings) {
      setSocialMedia(prev => prev.map(platform => ({
        ...platform,
        url: clubSettings[platform.key] || '',
        icon: clubSettings[`${platform.key}_icon`] || null
      })));
    }
  }, [clubSettings]);

  const showNotification = (message, type = 'success') => {
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
    const icon = type === 'success' ? '✅' : '❌';
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-4 rounded-xl shadow-2xl z-50`;
    notification.innerHTML = `
      <div class="flex items-center">
        <span class="text-xl mr-3">${icon}</span>
        <span class="font-medium">${message}</span>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 4000);
  };

  const handleUrlChange = (platformId, url) => {
    setSocialMedia(prev => prev.map(platform => 
      platform.id === platformId ? { ...platform, url } : platform
    ));

    // Update club settings
    const platform = socialMedia.find(p => p.id === platformId);
    if (platform) {
      setClubSettings(prev => ({
        ...prev,
        [platform.key]: url
      }));
    }
  };

  // Handle file preview before upload
  const handleFilePreview = (platformId, file) => {
    if (!file) return;

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    // Update platform with preview
    setSocialMedia(prev => prev.map(p => 
      p.id === platformId ? { ...p, uploadedFile: previewUrl } : p
    ));
  };

  const handleIconUpload = async (platformId, file) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      showNotification('حجم الملف كبير جداً (الحد الأقصى 5MB)', 'error');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showNotification('يجب أن يكون الملف صورة', 'error');
      return;
    }

    // Show preview first
    handleFilePreview(platformId, file);
    
    setUploading(prev => ({ ...prev, [platformId]: true }));

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.file_id) {
        // Update social media state
        setSocialMedia(prev => prev.map(platform => 
          platform.id === platformId ? { ...platform, icon: response.data.file_id } : platform
        ));

        // Update club settings
        const platform = socialMedia.find(p => p.id === platformId);
        if (platform) {
          setClubSettings(prev => ({
            ...prev,
            [`${platform.id}_icon_file`]: response.data.file_id
          }));
          
          // Update social media state to show the new icon immediately
          setSocialMedia(prev => prev.map(p => 
            p.id === platformId ? { ...p, icon: response.data.file_id } : p
          ));

          // 🔥 SAVE TO BACKEND AUTOMATICALLY - الحفظ التلقائي في Backend
          try {
            const settingsToSave = {
              [`${platform.id}_icon_file`]: response.data.file_id
            };

            await axios.put(`${API}/club-settings`, settingsToSave, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            showNotification(`تم رفع وحفظ أيقونة ${socialMedia.find(p => p.id === platformId)?.name} بنجاح! 🎉`);
            
            // Trigger refresh of public data
            if (window.dispatchEvent) {
              window.dispatchEvent(new CustomEvent('refreshPublicData'));
            }
          } catch (saveError) {
            console.error('خطأ في حفظ الأيقونة:', saveError);
            showNotification(`تم رفع الأيقونة لكن فشل الحفظ في النظام. يرجى الحفظ يدوياً.`, 'warning');
          }
        }
      }
    } catch (error) {
      console.error('خطأ في رفع الأيقونة:', error);
      showNotification('فشل في رفع الأيقونة', 'error');
    } finally {
      setUploading(prev => ({ ...prev, [platformId]: false }));
    }
  };

  const removeIcon = async (platformId) => {
    setSocialMedia(prev => prev.map(platform => 
      platform.id === platformId ? { ...platform, icon: null } : platform
    ));

    const platform = socialMedia.find(p => p.id === platformId);
    if (platform) {
      setClubSettings(prev => ({
        ...prev,
        [`${platform.id}_icon_file`]: null
      }));

      // 🔥 SAVE TO BACKEND AUTOMATICALLY - الحفظ التلقائي عند الحذف
      try {
        const token = localStorage.getItem('token');
        const settingsToSave = {
          [`${platform.id}_icon_file`]: null
        };

        await axios.put(`${API}/club-settings`, settingsToSave, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        showNotification(`تم حذف أيقونة ${socialMedia.find(p => p.id === platformId)?.name} وحفظ التغييرات 🎉`);
        
        // Trigger refresh of public data
        if (window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('refreshPublicData'));
        }
      } catch (error) {
        console.error('خطأ في حفظ حذف الأيقونة:', error);
        showNotification('تم حذف الأيقونة محلياً لكن فشل الحفظ في النظام. يرجى الحفظ يدوياً.', 'warning');
      }
    }
  };

  // Save settings to backend
  const saveToBackend = async () => {
    try {
      const token = localStorage.getItem('token');
      const settingsToSave = {};
      
      // Prepare social media URLs and icons
      socialMedia.forEach(platform => {
        if (platform.url) {
          settingsToSave[`${platform.id}_url`] = platform.url;
        }
        if (platform.icon) {
          settingsToSave[`${platform.id}_icon_file`] = platform.icon;
        }
      });

      await axios.put(`${API}/club-settings`, settingsToSave, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      showNotification('تم حفظ إعدادات وسائل التواصل بنجاح!');
    } catch (error) {
      console.error('خطأ في حفظ الإعدادات:', error);
      showNotification('فشل في حفظ الإعدادات', 'error');
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-2xl p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold text-gray-800 flex items-center">
            <span className="text-3xl mr-3">📱</span>
            إدارة روابط التواصل الاجتماعي
          </h3>
          <p className="text-gray-600 mt-1">رفع أيقونات مخصصة وإدارة روابط المنصات الاجتماعية</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {socialMedia.map((platform) => (
          <div key={platform.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
            {/* Platform Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                {platform.uploadedFile || platform.icon ? (
                  <img 
                    src={platform.uploadedFile || `${API}/files/${platform.icon}`}
                    alt={platform.name}
                    className="w-8 h-8 rounded-lg object-cover ring-2 ring-orange-200"
                  />
                ) : (
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: platform.color }}
                  >
                    <span className="text-sm">{platform.defaultIcon}</span>
                  </div>
                )}
                <h4 className="text-lg font-semibold text-gray-800 mr-3">{platform.name}</h4>
              </div>
              {platform.url && (
                <a 
                  href={platform.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 transition-colors"
                >
                  <span className="text-xl">🔗</span>
                </a>
              )}
            </div>

            {/* URL Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">رابط المنصة</label>
              <input
                type="url"
                value={platform.url}
                onChange={(e) => handleUrlChange(platform.id, e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={platform.placeholder}
              />
            </div>

            {/* Icon Management */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">أيقونة مخصصة</label>
              
              {platform.uploadedFile || platform.icon ? (
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <img 
                      src={platform.uploadedFile || `${API}/files/${platform.icon}`}
                      alt={platform.name}
                      className="w-10 h-10 rounded-lg object-cover ring-2 ring-green-200"
                    />
                    <span className="text-sm text-gray-600 mr-3">
                      {platform.uploadedFile ? 'معاينة الأيقونة الجديدة' : 'أيقونة مخصصة'}
                    </span>
                  </div>
                  <button
                    onClick={() => removeIcon(platform.id)}
                    className="text-red-500 hover:text-red-600 transition-colors"
                  >
                    <span className="text-lg">🗑️</span>
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFilePreview(platform.id, file);
                        handleIconUpload(platform.id, file);
                      }
                    }}
                    className="hidden"
                    id={`icon-upload-${platform.id}`}
                    disabled={uploading[platform.id]}
                  />
                  <label
                    htmlFor={`icon-upload-${platform.id}`}
                    className="cursor-pointer block"
                  >
                    {uploading[platform.id] ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                        <span className="text-sm text-gray-500">جاري الرفع...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <span className="text-2xl mb-2">📁</span>
                        <span className="text-sm font-medium text-gray-600">رفع أيقونة</span>
                        <span className="text-xs text-gray-400 mt-1">PNG, JPG (حد أقصى 5MB)</span>
                      </div>
                    )}
                  </label>
                </div>
              )}
            </div>

            {/* Preview */}
            {platform.url && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">معاينة</div>
                <div className="flex items-center">
                  {platform.icon ? (
                    <img 
                      src={`${API}/files/${platform.icon}`}
                      alt={platform.name}
                      className="w-6 h-6 rounded object-cover"
                    />
                  ) : (
                    <div 
                      className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: platform.color }}
                    >
                      {platform.defaultIcon}
                    </div>
                  )}
                  <span className="text-sm text-gray-700 mr-2 truncate">{platform.name}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-blue-50 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
          <span className="text-xl mr-2">💡</span>
          نصائح للاستخدام
        </h4>
        <ul className="text-sm text-blue-700 space-y-2">
          <li>• ارفع أيقونات مخصصة بتنسيق PNG أو JPG للحصول على مظهر احترافي</li>
          <li>• تأكد من أن الأيقونات مربعة الشكل (مثل 64x64 بكسل) لأفضل عرض</li>
          <li>• الروابط ستظهر في Footer الصفحة العامة تلقائياً</li>
          <li>• يمكنك ترك الرابط فارغاً لإخفاء المنصة من العرض</li>
        </ul>
      </div>
      
      {/* Save Button */}
      <div className="mt-6 text-center">
        <button
          onClick={saveToBackend}
          className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center mx-auto shadow-lg"
        >
          <span className="text-lg mr-2">💾</span>
          حفظ جميع الإعدادات
        </button>
      </div>
    </div>
  );
};

export default EnhancedSocialMediaManager;